import React, { createContext, useCallback, useContext, useMemo, useRef } from 'react';

import { fg } from '@confluence/feature-gating';

export type StaticMacroAttempt = 'initial' | 'edit' | 'insertion';

export type StaticMacroResultMetadata = {
	attempt: StaticMacroAttempt;
	version: number;
};

type StaticMacroStore = {
	html: Map<string, string>;
	errors: Map<string, string>;
	resultMetadata: Map<string, StaticMacroResultMetadata>;
	nextResultVersion: number;
	fullyLoaded: boolean;
	mountedLocalIds: Set<string>;
	insertionRefetchStartedAt: Map<string, number>;
	insertionRefetchGaveUp: Set<string>;
};

function createStaticMacroStore(): StaticMacroStore {
	return {
		html: new Map(),
		errors: new Map(),
		resultMetadata: new Map(),
		nextResultVersion: 0,
		fullyLoaded: false,
		mountedLocalIds: new Set(),
		insertionRefetchStartedAt: new Map(),
		insertionRefetchGaveUp: new Set(),
	};
}

/**
 * Builds a set of extensionKeys for Forge macro types that support staticView,
 * by reading the preloaded extensions from window.__SSR_FORGE_EXTENSIONS__.
 * An extension is considered static if it has a `static` property (e.g. `properties.static`).
 */
function buildStaticExtensionKeys(): Set<string> {
	const staticKeys = new Set<string>();
	try {
		const extensions = (window as any).__SSR_FORGE_EXTENSIONS__?.['xen:macro']?.extensions ?? [];
		for (const ext of extensions) {
			if (ext?.properties?.static != null) {
				staticKeys.add(ext.key);
			}
		}
	} catch {
		// Defensive: if window access fails, return empty set (all macros fall back to iframe)
	}
	return staticKeys;
}

type StaticMacroStoreUpdate = { type: 'static-macro-result' } | { type: 'fully-loaded' };
type Listener = (update: StaticMacroStoreUpdate) => void;

// Relying on lots of refs because we want to limit re-renders.
export type StaticMacroStoreContextValue = {
	storeRef: React.RefObject<StaticMacroStore>;
	subscribe: (localId: string, cb: Listener) => () => void;
	register: (
		localId: string,
		html: string,
		options?: { force?: boolean; attempt?: StaticMacroAttempt },
	) => void;
	registerError: (
		localId: string,
		errorCode: string,
		options?: { force?: boolean; attempt?: StaticMacroAttempt },
	) => void;
	markFullyLoaded: () => void;
	isStaticExtension: (extensionKey: string) => boolean;
	hasMountedBefore: (localId: string) => boolean;
	markMounted: (localId: string) => void;
	triggerRefetch: (localId: string, attempt: StaticMacroAttempt) => void;
	setRefetchTrigger: (fn: ((localId: string, attempt: StaticMacroAttempt) => void) | null) => void;
};

const StaticMacroStoreContext = createContext<StaticMacroStoreContextValue | null>(null);

// StaticMacroStoreProvider is designed to let us populate localIds with static HTML as we receive it from the network.
// If HTML is not provided, or an error is returned for a specific localId, we fallback to the iframe.
// The flow looks like:
// `ForgeMacro` subscribes a localId to listen to the store
// `StaticMacroDataBridge` checks for static data coming back from the monolith.
// For each node with static data, we `.register` and save the HTML. Any failures we call `.registerError` to signal falling back to the iframe
// Once all pages are received, we `.markFullyLoaded` to trigger any pending localIds from `ForgeMacro` to render via iframe.
export function StaticMacroStoreProvider({
	children,
}: {
	children: React.ReactNode;
}): React.ReactElement {
	const storeRef = useRef<StaticMacroStore>(createStaticMacroStore());
	const listenersRef = useRef<Map<string, Set<Listener>>>(new Map()); // localId -> listener callbacks
	const fullyLoadedListenersRef = useRef<Set<Listener>>(new Set());
	const getStaticExtensionKeys = useCallback((): Set<string> => buildStaticExtensionKeys(), []);

	const subscribe = useCallback((localId: string, cb: Listener): (() => void) => {
		const listeners = listenersRef.current;
		if (!listeners.has(localId)) {
			listeners.set(localId, new Set());
		}
		listeners.get(localId)!.add(cb);
		fullyLoadedListenersRef.current.add(cb);

		return () => {
			listeners.get(localId)?.delete(cb);
			fullyLoadedListenersRef.current.delete(cb);
		};
	}, []);

	const register = useCallback(
		(
			localId: string,
			html: string,
			options?: { force?: boolean; attempt?: StaticMacroAttempt },
		) => {
			const store = storeRef.current;
			if (store.html.has(localId) && !options?.force) return;
			store.html.set(localId, html);
			// A forced (refetch) registration supersedes any previous error for this localId too.
			if (options?.force) {
				store.errors.delete(localId);
			}
			store.resultMetadata.set(localId, {
				attempt: options?.attempt ?? 'initial',
				version: store.nextResultVersion++,
			});
			listenersRef.current.get(localId)?.forEach((cb) => cb({ type: 'static-macro-result' }));
		},
		[],
	);

	const registerError = useCallback(
		(
			localId: string,
			errorCode: string,
			options?: { force?: boolean; attempt?: StaticMacroAttempt },
		) => {
			const store = storeRef.current;
			if (store.errors.has(localId) && !options?.force) return;
			store.errors.set(localId, errorCode);
			if (options?.force) {
				store.html.delete(localId);
			}
			store.resultMetadata.set(localId, {
				attempt: options?.attempt ?? 'initial',
				version: store.nextResultVersion++,
			});
			listenersRef.current.get(localId)?.forEach((cb) => cb({ type: 'static-macro-result' }));
		},
		[],
	);

	const markFullyLoaded = useCallback(() => {
		storeRef.current.fullyLoaded = true;
		fullyLoadedListenersRef.current.forEach((cb) => cb({ type: 'fully-loaded' }));
	}, []);

	const isStaticExtension = useCallback(
		(extensionKey: string) => getStaticExtensionKeys().has(extensionKey),
		[getStaticExtensionKeys],
	);

	const hasMountedBefore = useCallback(
		(localId: string) => storeRef.current.mountedLocalIds.has(localId),
		[],
	);

	const markMounted = useCallback((localId: string) => {
		storeRef.current.mountedLocalIds.add(localId);
	}, []);

	const refetchTriggerRef = useRef<((localId: string, attempt: StaticMacroAttempt) => void) | null>(
		null,
	);

	const triggerRefetch = useCallback((localId: string, attempt: StaticMacroAttempt) => {
		refetchTriggerRef.current?.(localId, attempt);
	}, []);

	const setRefetchTrigger = useCallback(
		(fn: ((localId: string, attempt: StaticMacroAttempt) => void) | null) => {
			refetchTriggerRef.current = fn;
		},
		[],
	);

	const contextValue: StaticMacroStoreContextValue = useMemo(
		() => ({
			storeRef,
			subscribe,
			register,
			registerError,
			markFullyLoaded,
			isStaticExtension,
			hasMountedBefore,
			markMounted,
			triggerRefetch,
			setRefetchTrigger,
		}),
		[
			storeRef,
			subscribe,
			register,
			registerError,
			markFullyLoaded,
			isStaticExtension,
			hasMountedBefore,
			markMounted,
			triggerRefetch,
			setRefetchTrigger,
		],
	);

	return (
		<StaticMacroStoreContext.Provider
			value={fg('conf_frontend_forge_static_macro_mvp') ? contextValue : null}
		>
			{children}
		</StaticMacroStoreContext.Provider>
	);
}

export function useStaticMacroStoreContext(): StaticMacroStoreContextValue | null {
	return useContext(StaticMacroStoreContext);
}

// This is used in the Editor when a macro is having it's configuration changed so that the cached HTML isn't shown.
export function ResetStaticMacroStoreContext({
	children,
}: {
	children: React.ReactNode;
}): React.ReactElement {
	return (
		<StaticMacroStoreContext.Provider value={null}>{children}</StaticMacroStoreContext.Provider>
	);
}
